import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@dmca/frontend/components/button";
import {
  NewPasswordFormFields,
  passwordFields,
} from "@dmca/frontend/components/input";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { Alert, Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import {
  InferOutput,
  object,
  pipe,
  forward,
  partialCheck,
  optional,
  string,
} from "valibot";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { authClient } from "@dmca/frontend/hooks";

export const Route = createFileRoute("/_login/reset-password")({
  component: RouteComponent,
  validateSearch: object({
    token: optional(string()),
    error: optional(string()),
  }),
  head: () => ({
    meta: [
      {
        title: "Reset Password | DNA Dashboard",
      },
    ],
  }),
});

const formData = pipe(
  object({
    ...passwordFields.entries,
  }),
  forward(
    partialCheck(
      [["password"], ["confirmPassword"]],
      (input) => input.password === input.confirmPassword,
      "Passwords do not match."
    ),
    ["confirmPassword"]
  )
);
type FormFields = InferOutput<typeof formData>;

function RouteComponent() {
  const { token, error } = Route.useSearch();
  const { control, handleSubmit } = useForm<FormFields>({
    resolver: valibotResolver(formData),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const mutation = useMutation({
    mutationKey: ["auth", "resetPassword"],
    mutationFn: async (data: FormFields) => {
      if (!token || !!error) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (error) throw new Error(error);
        throw new Error("No token provided.");
      }

      const resp = await authClient.resetPassword({
        token,
        newPassword: data.password,
      });

      if (resp.error) throw resp.error;
      return resp.data;
    },
    onSuccess: () => {
      setSuccess(true);
      setErrorMessage("");
    },
    onError: (error: {
      code?: string | undefined;
      message?: string | undefined;
      status: number;
      statusText: string;
    }) => {
      console.error(error);
      if (error.code === "INVALID_TOKEN") {
        setErrorMessage("The reset link is invalid or has expired.");
      } else {
        setErrorMessage(
          "An unknown error occured. Please try again or contact support."
        );
      }
    },
  });

  const handleFormSubmit = (data: FormFields) => {
    mutation.mutate(data);
  };

  if (success) {
    return (
      <>
        <Typography
          sx={{ mt: 4, mb: 3, typography: "pageTitle", fontWeight: 700 }}
        >
          Password Reset Successful
        </Typography>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            Your password has been reset
          </Typography>
          <Typography variant="body2">
            You can now sign in with your new password.
          </Typography>
        </Alert>
        <Button href="/login" variant="outlined" sx={{ mt: 2 }} size="small">
          Go to Login
        </Button>
      </>
    );
  }

  return (
    <>
      <Typography
        sx={{ mt: 4, mb: 1, typography: "pageTitle", fontWeight: 700 }}
      >
        Reset Your Password
      </Typography>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}
      <Typography sx={{ mb: 3, color: "text.secondary" }}>
        Enter your new password below.
      </Typography>
      <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
        <NewPasswordFormFields control={control} autofocus />
        <Button
          fullWidth
          type="submit"
          loading={mutation.isPending}
          sx={{ mt: 2 }}
        >
          Reset Password
        </Button>
      </Box>
    </>
  );
}
